import React, { useEffect, useRef, useState } from 'react';
import { ReviewQueueEntry } from 'api/DataReview';
import SidePanel from 'Components/DesignSystem/SidePanel/SidePanel';
import IconButton from 'Components/DesignSystem/IconButton/IconButton';
import Alert from 'Components/DesignSystem/Alert/Alert';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { Refresh, Cross } from 'Components/DesignSystem/Iconography/Icons';
import ReviewQueueTable from './ReviewQueueTable';
import ReviewQueueLogModal from './ReviewQueueLogModal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  entries: ReviewQueueEntry[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  onRefresh: () => void;
  onLoadMore: () => Promise<void>;
  onRefreshEntry: (uid: string) => Promise<void>;
  onRetry: (uid: string) => void;
}

/**
 * The Review Queue drawer, on the DS `SidePanel` (2026-09-05) — the § 109
 * RulesQueue shape, whose `xl` width EXISTS for this exact 66%-wide
 * table-in-a-drawer case. It was a hand-rolled `top:60` fixed strip on
 * `tw-bg-white` with its own backdrop — light-only, no Escape, no focus
 * management. The panel brings the scrim, Escape (LIFO-stacked, so with the
 * log panel open Escape closes the LOG first), focus handling and the themed
 * `surface-raised` ground; Refresh and the X keep their legacy testids in
 * `headerActions`. Lists every Data Review action the user has dispatched on
 * this channel, with View Log + Retry.
 */
const ReviewQueueDrawer: React.FC<Props> = ({
  isOpen,
  onClose,
  entries,
  loading,
  loadingMore,
  hasMore,
  error,
  onRefresh,
  onLoadMore,
  onRefreshEntry,
  onRetry,
}) => {
  const [entryRefreshing, setEntryRefreshing] = useState(false);
  // Track the open log by uid (not by a captured ReviewQueueEntry object)
  // so the View Log panel's content stays in sync with the queue list when
  // the user hits refresh — the entry is re-derived from `entries` on every
  // render, so a refetched row automatically replaces the displayed one.
  const [logEntryUid, setLogEntryUid] = useState<string | null>(null);
  const logEntry = logEntryUid ? entries.find((e) => e.uid === logEntryUid) || null : null;

  // IntersectionObserver-driven infinite scroll. The sentinel is rendered
  // at the bottom of the list; when it enters the scroll viewport and
  // there's still more data, fire onLoadMore. The observer re-attaches
  // when the sentinel ref changes (i.e., when entries change).
  // The root is `null` (the viewport): the SidePanel owns the scroll
  // container now, and the IO spec clips `intersectionRect` by every
  // `overflow` ancestor — the sentinel only "intersects the viewport" once
  // the panel has actually scrolled it into view, which is the same firing
  // condition the old explicit-root observer had.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!isOpen) return undefined;
    const node = sentinelRef.current;
    if (!node) return undefined;
    const obs = new IntersectionObserver(
      (entriesList) => {
        if (entriesList[0]?.isIntersecting && hasMore && !loading && !loadingMore) {
          onLoadMore();
        }
      },
      { root: null, rootMargin: '120px' },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [isOpen, hasMore, loading, loadingMore, onLoadMore, entries.length]);

  // Per-entry polling for the open View Log. While the panel is open and
  // its entry is still Pending / In Progress, refresh that single row
  // every 5s so the user sees status + log progress without clicking
  // refresh. Stops as soon as the row reaches a terminal state, so a
  // Failed/Completed log panel doesn't keep polling forever.
  const logEntryStatus = logEntry?.status;
  const logEntryActive =
    logEntryStatus === 'Pending' || logEntryStatus === 'In Progress';
  useEffect(() => {
    if (!isOpen || !logEntryUid || !logEntryActive) return undefined;
    const id = setInterval(() => {
      onRefreshEntry(logEntryUid);
    }, 5000);
    return () => clearInterval(id);
  }, [isOpen, logEntryUid, logEntryActive, onRefreshEntry]);

  if (!isOpen) return null;

  return (
    <>
      <SidePanel
        isOpen
        onClose={onClose}
        width="xl"
        title="Review Queue"
        tabs={[]}
        data-testid="review-queue-drawer"
        headerActions={
          <>
            <IconButton
              aria-label="Refresh"
              data-testid="review-queue-reviewqueuedrawer-button-1"
              disabled={loading}
              onClick={onRefresh}
            >
              <Refresh className={loading ? 'tw-animate-spin' : undefined} />
            </IconButton>
            <IconButton
              aria-label="Close review queue"
              data-testid="review-queue-reviewqueuedrawer-button-2"
              onClick={onClose}
            >
              <Cross />
            </IconButton>
          </>
        }
      >
        {error ? (
          <div className="tw-mb-3">
            <Alert tone="danger" data-testid="review-queue-error">{error}</Alert>
          </div>
        ) : null}
        {loading && entries.length === 0 ? (
          <Typography variant="body-primary" className="tw-text-ink-secondary tw-py-4" as="div">
            Loading…
          </Typography>
        ) : (
          <>
            <ReviewQueueTable
              entries={entries}
              onViewLog={(e: ReviewQueueEntry) => setLogEntryUid(e.uid)}
              onRetry={(e) => onRetry(e.uid)}
            />
            <div ref={sentinelRef} aria-hidden="true" className="tw-h-px" />
            {loadingMore ? (
              <div className="tw-py-4 tw-text-ink-secondary tw-text-sm tw-text-center">Loading more…</div>
            ) : null}
            {!hasMore && entries.length > 0 ? (
              <div className="tw-py-3 tw-text-ink-secondary tw-text-xs tw-text-center">End of queue</div>
            ) : null}
          </>
        )}
      </SidePanel>
      <ReviewQueueLogModal
        entry={logEntry}
        onClose={() => setLogEntryUid(null)}
        onRefresh={async () => {
          if (!logEntryUid) return;
          setEntryRefreshing(true);
          try {
            await onRefreshEntry(logEntryUid);
          } finally {
            setEntryRefreshing(false);
          }
        }}
        refreshing={entryRefreshing}
      />
    </>
  );
};

export default ReviewQueueDrawer;
